import { useErrorMessages } from '@AppModels/hooks/appStateHooks';
import { IModalContext, ModalChildComponentContextKey } from '@AppModels/index';
import { useEventLogging } from '@AppModels/Logging';
import { useContactRemoveTagsMutation } from '@Queries/Contact/useContactRemoveTagsMutation';
import { useGetSystemJobQuery } from '@Queries/SystemJob/useGetSystemJobQuery';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { asModalComponent } from '@WebComponents/Modal';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Pill } from '../../../components-v2/Pill/Pill';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { styleSheet } from './styles';

interface Props extends IModalContext<string> {
	tagName: string;
	request?: Api.IBulkContactsRequest;
	totalContactCount?: number;
}

export function _ContactsRemoveTag({ request, tagName, totalContactCount, parentModal }: Props) {
	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('ContactsRemoveTag');

	const contactRemoveTagsMutation = useContactRemoveTagsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('ContactRemoveTagsMutation', error);
		},
	});

	const systemJobQuery = useGetSystemJobQuery({
		enabled: contactRemoveTagsMutation.isSuccess,
		jobId: contactRemoveTagsMutation.data?.id,
		refetchInterval: query => {
			const data = query.state.data;
			if (!data) {
				return false;
			}
			if (data.percentComplete < 100) {
				return 2000;
			}
			return false;
		},
	});

	const isSystemJobComplete =
		systemJobQuery.data?.percentComplete != null && systemJobQuery.data?.percentComplete >= 100;

	React.useEffect(() => {
		if (isSystemJobComplete) {
			parentModal?.onRequestClose(tagName, false);
		}
	}, [isSystemJobComplete, tagName, parentModal]);

	const onCloseClicked = () => {
		if (parentModal) {
			parentModal.onRequestClose(null, true);
		}
	};

	const onConfirmRemovalClicked = async () => {
		if (!tagName || contactRemoveTagsMutation.isPending) {
			return;
		}

		logInput('Remove', 'Click', { tagName });
		contactRemoveTagsMutation.mutateAsync({
			request: {
				...request,
				tags: [tagName],
			},
		});
	};

	// Calculate the number of contacts affected
	const contactCount = React.useMemo(() => {
		// If totalContactCount is provided (when selecting all), use it
		if (totalContactCount !== undefined) {
			return totalContactCount;
		}
		// Otherwise use the includeContactIds length
		if (request?.includeContactIds && request.includeContactIds.length > 0) {
			return request.includeContactIds.length;
		}
		return 'selected';
	}, [request, totalContactCount]);

	return (
		<div className={`${css(styleSheet.container)} contacts-remove-tag`}>
			<div className={css(styleSheet.header)}>
				<DeprecatedCloseButton
					disabled={
						contactRemoveTagsMutation.isPending || (contactRemoveTagsMutation.isSuccess && !isSystemJobComplete)
					}
					onClick={onCloseClicked}
				/>
			</div>
			<div className={css(styleSheet.title)}>Remove Tag</div>
			{contactRemoveTagsMutation.isSuccess && !isSystemJobComplete ? (
				<div className={css(styleSheet.removingMessage)}>
					<div>Removing tag...</div>
					<LoadingSpinner type='small' />
				</div>
			) : (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.message)}>
						Remove <Pill variant='tag' label={tagName} color='blue' /> from{' '}
						{typeof contactCount === 'number' ? `${contactCount} contacts` : `${contactCount} contacts`}?
					</div>
					<div className={css(styleSheet.subMessage)}>
						This action cannot be undone. The contacts will remain but the tag will be removed.
					</div>
					<div className={css(styleSheet.buttonContainer)}>
						<button
							className={css(baseStyleSheet.ctaButtonReverse, styleSheet.cancelButton)}
							onClick={onCloseClicked}
							disabled={contactRemoveTagsMutation.isPending}
						>
							<span>Cancel</span>
						</button>
						<button
							className={css(baseStyleSheet.ctaButtonDestructiveSolid, styleSheet.confirmButton)}
							onClick={onConfirmRemovalClicked}
							disabled={contactRemoveTagsMutation.isPending}
						>
							<span>{contactRemoveTagsMutation.isPending ? 'Removing...' : 'Confirm Removal'}</span>
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

const ContactsRemoveTagAsObserver = observer(_ContactsRemoveTag);
export const ContactsRemoveTag = inject(ModalChildComponentContextKey)(ContactsRemoveTagAsObserver);

export const ContactsRemoveTagModal = asModalComponent(ContactsRemoveTag, {
	className: 'contacts-remove-tag-modal',
	shouldCloseOnOverlayClick: false,
});
